import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { errorMessage } from "../../api/client";
import { useCreateNote, usePlayerNotes } from "../../api/queries";
import type { Player, PlayerNote } from "../../api/models";
import { DictationField } from "../../dictation/DictationField";
import { PButton } from "../../ui/PButton";
import { PConfirmDialog } from "../../ui/PConfirmDialog";
import { PDropdown } from "../../ui/PDropdown";
import { PSidebar } from "../../ui/PSidebar";
import { PTabView } from "../../ui/PTabView";
import { CountHeader, PAccordion, PCheckbox, PPagedList } from "../../ui/PWidgets";
import { useToast } from "../../ui/toast";
import {
  generatePreferences,
  useCreatePreference,
  useCreatePreferences,
  useDeleteNote,
  useDeletePreference,
  usePreferenceCategories,
  usePreferences,
  useUpdatePreference,
  type Preference,
} from "../profile-api";
import { localStamp } from "./ActivityGreetSidebars";

const DICTATING = "Dictation is still in progress. Stop it to keep what you said, or clear the field, then try again.";

// `player-notes-prefs` IN A MODAL RIGHT SIDEBAR: NOTES, PREFERENCES AND GENERATE TABS. EVERY
// TAB CHANGE, AND COLLAPSING A TAB'S ACCORDION, ABANDONS DICTATION - A HIDDEN FIELD MUST NOT
// KEEP RECORDING WITH ITS STOP BUTTON OFF SCREEN.
// PLAYER COMMUNICATIONS OPENS IT FROM THE LEFT WITH MASK CLICKS AND ESCAPE DISABLED; PILLARS ASSIST
// OPENS IT NON-MODAL, ONCE ON NOTES AND ONCE ON PREFERENCES (`currentTabIndex`)
export function NotesPrefsSidebar({ player, open, onClose, position = "right", dismissible = true, modal = true, initialTab = 0 }: { player: Player; open: boolean; onClose: () => void; position?: "left" | "right"; dismissible?: boolean; modal?: boolean; initialTab?: number }) {
  const [tab, setTab] = useState(initialTab);
  // BUMPED TO ABANDON DICTATION IN WHATEVER FIELD IS RECORDING
  const [hideToken, setHideToken] = useState(0);
  const hidden = () => setHideToken((n) => n + 1);
  return (
    <PSidebar
      open={open}
      onClose={() => {
        hidden();
        onClose();
      }}
      position={position}
      dismissible={dismissible}
      closeOnEscape={dismissible}
      modal={modal}
      width={{ xs: "100%", md: "30rem" }}
      ariaLabel="Notes and preferences"
    >
      <PTabView
        tabs={["Notes", "Preferences", "Generate"]}
        active={tab}
        onChange={(i) => {
          hidden();
          setTab(i);
        }}
        label={(t) => t}
      >
        {tab === 0 && <NotesTab player={player} hideToken={hideToken} onHide={hidden} />}
        {tab === 1 && <PreferencesTab player={player} />}
        {tab === 2 && <GenerateTab player={player} hideToken={hideToken} />}
      </PTabView>
    </PSidebar>
  );
}

function NotesTab({ player, hideToken, onHide }: { player: Player; hideToken: number; onHide: () => void }) {
  const toast = useToast();
  const cms = player.cmsPlayerId || undefined;
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState("");
  const [dictating, setDictating] = useState(false);
  const [deleting, setDeleting] = useState<PlayerNote | null>(null);
  const notes = usePlayerNotes(cms, page);
  const createNote = useCreateNote(cms);
  const deleteNote = useDeleteNote(cms);
  const data = notes.data;

  if (!cms) {
    // THE ANGULAR PANEL GUARDS THE SAME CASE (#84): POSTING WOULD ORPHAN THE NOTE
    return <div className="mt-3 pillars-dataview-empty">This player has no CMS player id, so notes are unavailable.</div>;
  }

  const add = () => {
    if (dictating) {
      toast("warning", "Dictation", DICTATING);
      return;
    }
    createNote.mutate(message, {
      onSuccess: () => {
        toast("success", "Add Note", "Note was successfully added");
        setMessage("");
        setPage(1);
      },
      onError: () => toast("error", "Add Note", "Note was not successfully added, please try again."),
    });
  };

  const remove = () => {
    const note = deleting;
    setDeleting(null);
    if (!note) return;
    deleteNote.mutate(note, {
      onSuccess: () => toast("success", "Delete Note", "Note was successfully deleted"),
      onError: () => toast("error", "Delete Note", "Note was not successfully deleted, please try again."),
    });
  };

  return (
    <div className="parent">
      <div className="child">
        <PAccordion className="mt-3" onCollapse={onHide} header={<CountHeader label="Note" count={data?.totalRows ?? 0} />}>
          <div>
            <DictationField
              value={message}
              onChange={setMessage}
              label="Note:"
              rows={5}
              maxLength={150}
              helperText="Speak, paste or enter note"
              onBusyChange={setDictating}
              cancelToken={hideToken}
            />
          </div>
          <div className="mt-3 flex flex-row gap-3 align-items-center justify-content-between">
            <div className="w-full">
              <PButton label="Clear" severity="warning" hostElement disabled={!message} onClick={() => setMessage("")} sx={{ fontWeight: 700 }} />
            </div>
            <div className="w-full">
              {/* DISABLED WHILE DICTATING: ADDING WOULD POST WITHOUT THE PENDING TRANSCRIPT */}
              <PButton label="Add" className="w-full" hostElement disabled={!message || dictating} loading={createNote.isPending} onClick={add} sx={{ fontWeight: 700 }} />
            </div>
          </div>
        </PAccordion>
      </div>
      <div className="child-grow card mt-4 h-full p-0 overflow-x-hidden overflow-y-auto">
        <PPagedList
          page={data?.page ?? page}
          totalPages={data?.totalPages ?? 1}
          totalRows={data?.totalRows ?? 0}
          rows={data?.rows}
          loading={notes.isPending}
          error={notes.isError}
          emptyMessage="No notes available"
          onPage={setPage}
          rowKey={(n) => n.id}
          render={(n) => <NoteRow note={n} onDelete={() => setDeleting(n)} />}
        />
      </div>
      <PConfirmDialog
        open={deleting !== null}
        header="Delete Note"
        message="Are you sure you want to delete this note?"
        acceptLabel="Yes"
        rejectLabel="No"
        onAccept={remove}
        onReject={() => setDeleting(null)}
      />
    </div>
  );
}

// `note-template`: DATE ROW WITH A ROUND DANGER TRASH BUTTON, THEN HOST, THEN THE MESSAGE
function NoteRow({ note, onDelete }: { note: PlayerNote; onDelete: () => void }) {
  return (
    <div>
      <div className="text-600 flex align-items-center justify-content-between">
        <div className="flex align-items-center">
          <span className="material-symbols-outlined mr-2">calendar_month</span>
          <div>{localStamp(note.created_at)}</div>
        </div>
        <div>
          <PButton icon="trash" rounded variant="text" severity="danger" ariaLabel="Delete note" onClick={onDelete} />
        </div>
      </div>
      <div className="mt-2 text-600 flex flex-row align-items-center justify-content-between">
        <div className="flex flex-row align-items-center">
          <span className="material-symbols-rounded">account_circle</span>
          <div>{note.hostName}</div>
        </div>
      </div>
      <div className="mt-3 text-600 flex flex-row align-items-center">
        <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{note.message}</div>
      </div>
    </div>
  );
}

// PREFERENCES: AN OPEN ACCORDION TO ADD ONE (CATEGORY + A 150-CHARACTER VALUE), OVER THE LIST,
// WHERE EACH VALUE IS EDITABLE IN PLACE AND SAVED OR DELETED FROM ITS ROW
function PreferencesTab({ player }: { player: Player }) {
  const toast = useToast();
  const patronId = player.patronId;
  const categories = usePreferenceCategories();
  const prefs = usePreferences(patronId);
  const create = useCreatePreference(patronId);
  const update = useUpdatePreference(patronId);
  const del = useDeletePreference(patronId);
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [value, setValue] = useState("");
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [deleting, setDeleting] = useState<Preference | null>(null);

  const rows = patronId ? (prefs.data ?? []) : [];
  const category = categories.data?.find((c) => c.id === categoryId);

  const add = () => {
    if (!category || !patronId) return;
    create.mutate(
      { id: 0, patronId, patronPreferenceCategoryId: category.id, patronPreferenceCategoryCode: category.code, patronPreferenceCategoryName: category.name, value },
      {
        onSuccess: () => {
          toast("success", "Create Player Preference", "Player Preference was created successfully");
          setCategoryId("");
          setValue("");
        },
        onError: (e) => toast("error", "Create Player Preference", errorMessage(e)),
      },
    );
  };

  const save = (p: Preference) =>
    update.mutate(
      { ...p, value: edits[p.id] ?? p.value },
      {
        // THE TRAILING SPACE IS ANGULAR'S
        onSuccess: () => toast("success", "Update Player Preferences", "Player Preference was updated successfully "),
        onError: (e) => toast("error", "Update Player Preferences", errorMessage(e)),
      },
    );

  const remove = () => {
    const p = deleting;
    setDeleting(null);
    if (!p) return;
    del.mutate(p, {
      onSuccess: () => toast("success", "Delete Player Preference", "Player Preference was deleted successfully"),
      onError: (e) => toast("error", "Delete Player Preference", errorMessage(e)),
    });
  };

  return (
    <div className="parent">
      <div className="child">
        <PAccordion className="mt-3" header={<CountHeader label="Preferences" count={rows.length} />}>
          <div>
            <div className="font-bold">Preference Category:</div>
            <div className="flex align-items-center gap-2 mt-1">
              <PDropdown
                value={categoryId}
                options={(categories.data ?? []).map((c) => ({ label: c.name, value: c.id }))}
                placeholder="Select a preference category"
                onChange={setCategoryId}
              />
              {categoryId !== "" && <PButton icon="times" rounded variant="text" severity="secondary" ariaLabel="Clear category" onClick={() => setCategoryId("")} />}
            </div>
          </div>
          <div className="w-full mt-3">
            <div className="font-bold">Preference Value:</div>
            <textarea
              className="p-inputtext w-full mt-1"
              rows={3}
              style={{ resize: "none" }}
              maxLength={150}
              placeholder="Enter a preference value"
              aria-label="Preference Value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="mt-4">
            <PButton label="Add" className="w-full" hostElement disabled={!value || !category} loading={create.isPending} onClick={add} sx={{ fontWeight: 700 }} />
          </div>
        </PAccordion>
      </div>
      <div className="child-grow card mt-4 h-full p-0 overflow-x-hidden overflow-y-auto">
        {prefs.isPending && patronId ? (
          <div className="flex justify-content-center p-4">
            <CircularProgress size={28} />
          </div>
        ) : rows.length === 0 ? (
          <div className="pillars-dataview-empty">No preferences available</div>
        ) : (
          <div className="grid grid-nogutter">
            {rows.map((p, i) => (
              <div key={p.id} className="col-12">
                <div className={`p-3${i > 0 ? " border-top-1 surface-border" : ""}`}>
                  <div className="flex gap-2 align-items-center justify-content-between">
                    <div className="font-bold">{p.patronPreferenceCategoryName}</div>
                    <div className="flex justify-content-end">
                      <Tooltip title="Save" placement="top">
                        <span>
                          <PButton icon="save" rounded variant="text" ariaLabel="Save preference" onClick={() => save(p)} />
                        </span>
                      </Tooltip>
                      <PButton icon="trash" rounded variant="text" severity="danger" ariaLabel="Delete preference" onClick={() => setDeleting(p)} />
                    </div>
                  </div>
                  <div className="text-sm w-full">
                    <textarea
                      className="p-inputtext w-full mt-1"
                      rows={3}
                      style={{ resize: "none" }}
                      maxLength={150}
                      placeholder="Enter a preference value"
                      aria-label={`${p.patronPreferenceCategoryName ?? "Preference"} value`}
                      value={edits[p.id] ?? p.value ?? ""}
                      onChange={(e) => setEdits((x) => ({ ...x, [p.id]: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <PConfirmDialog
        open={deleting !== null}
        header="Delete Preference"
        message="Are you sure you want to delete this preference?"
        acceptLabel="Yes"
        rejectLabel="No"
        onAccept={remove}
        onReject={() => setDeleting(null)}
      />
    </div>
  );
}

type Generated = Preference & { selected: boolean };

// GENERATE: DICTATE OR PASTE UP TO 5,000 CHARACTERS, HAVE THE CORE API SUGGEST PREFERENCES FROM
// IT, THEN PICK, RE-CATEGORIZE OR EDIT THE SUGGESTIONS AND CREATE THE SELECTED ONES
function GenerateTab({ player, hideToken }: { player: Player; hideToken: number }) {
  const toast = useToast();
  const categories = usePreferenceCategories();
  const createMany = useCreatePreferences(player.patronId);
  const [text, setText] = useState("");
  const [dictating, setDictating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<Generated[]>([]);
  // `textAreaRows`: 15, OR 5 ON A PHONE
  const [rows] = useState(() => (typeof window !== "undefined" && window.innerWidth < 768 ? 5 : 15));

  const clear = () => {
    setText("");
    setGenerated([]);
  };

  const generate = async () => {
    if (dictating) {
      toast("warning", "Dictation", DICTATING);
      return;
    }
    setGenerated([]);
    setGenerating(true);
    try {
      const out = await generatePreferences(text);
      setGenerated(out.map((p) => ({ ...p, selected: false })));
      setText("");
    } catch (e) {
      toast("error", "Generate Player Preferences", errorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const selected = generated.filter((g) => g.selected);
  const allSelected = generated.length > 0 && generated.every((g) => g.selected);
  const setRow = (i: number, patch: Partial<Generated>) => setGenerated((list) => list.map((g, j) => (j === i ? { ...g, ...patch } : g)));

  const createSelected = () => {
    // A PLAYER WITH NO `patronId` CANNOT OWN A PREFERENCE (#84)
    if (!player.patronId) return;
    if (dictating) {
      toast("warning", "Dictation", DICTATING);
      return;
    }
    createMany.mutate(
      selected.map(({ selected: _s, ...p }) => ({ ...p, patronId: player.patronId })),
      {
        onSuccess: () => {
          toast("success", "Create Player Preferences", "Player Preferences were successfully created");
          clear();
        },
        onError: (e) => toast("error", "Create Player Preferences", errorMessage(e)),
      },
    );
  };

  return (
    <div className="parent">
      <div className="child">
        <div className="mt-4">
          <DictationField
            value={text}
            onChange={setText}
            label="Generate Preferences:"
            rows={rows}
            maxLength={5000}
            helperText="Speak, paste or enter content to generate preferences."
            onBusyChange={setDictating}
            cancelToken={hideToken}
          />
        </div>
        <div className="mt-3 flex flex-row align-items-center justify-content-between">
          <PButton label="Clear" severity="warning" hostElement disabled={!text} onClick={clear} sx={{ fontWeight: 700 }} />
          <PButton label="Generate Preferences" icon="microchip-ai" hostElement disabled={!text || dictating} loading={generating} onClick={() => void generate()} sx={{ fontWeight: 700 }} />
        </div>
        {generated.length > 0 && (
          <div className="mt-4">
            <div className="font-bold mb-1">Preferences:</div>
            <div className="mt-3">
              <PButton label="Create Preferences" className="w-full" hostElement disabled={selected.length === 0 || dictating} loading={createMany.isPending} onClick={createSelected} sx={{ fontWeight: 700 }} />
            </div>
          </div>
        )}
      </div>
      {generated.length > 0 && (
        <div className="mt-3 child-grow card mt-4 h-full p-0 overflow-x-hidden overflow-y-auto">
          <div className="pillars-dataview-header">
            <div className="flex flex-column md:flex-row md:justify-content-between">
              <div className="flex align-items-center">
                <PCheckbox id="selectAll" checked={allSelected} onChange={(v) => setGenerated((list) => list.map((g) => ({ ...g, selected: v })))} />
                <label htmlFor="selectAll" className="ml-2 font-bold">
                  Select All Preferences
                </label>
              </div>
            </div>
          </div>
          <div className="grid grid-nogutter">
            {generated.map((g, i) => (
              <div key={i} className="col-12">
                <div className="flex flex-row p-2 gap-2 align-items-center justify-content-between border-bottom-1 surface-border">
                  <div className="w-4rem">
                    <PCheckbox checked={g.selected} onChange={(v) => setRow(i, { selected: v })} ariaLabel={`Select ${g.value ?? "preference"}`} />
                  </div>
                  <div className="w-full flex flex-column gap-2">
                    <PDropdown
                      value={g.patronPreferenceCategoryId ?? ""}
                      options={(categories.data ?? []).map((c) => ({ label: c.name, value: c.id }))}
                      ariaLabel="Preference category"
                      onChange={(v) => {
                        const c = categories.data?.find((x) => x.id === v);
                        setRow(i, { patronPreferenceCategoryId: v, patronPreferenceCategoryCode: c?.code, patronPreferenceCategoryName: c?.name });
                      }}
                    />
                    <div className="text-sm">
                      <textarea
                        className="p-inputtext w-full mt-1"
                        rows={3}
                        style={{ resize: "none" }}
                        maxLength={150}
                        aria-label="Preference value"
                        value={g.value ?? ""}
                        onChange={(e) => setRow(i, { value: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
